/**
 * @file Contains hook for populating current topic deleted or not selected
 */

import { useEffect } from 'react';

import { TopicBasicFragment } from '../../../../generated/graphql';
import useParamTopicId from '../../../../hooks/router/params/useParamTopicId';
import useToast from '../../../../hooks/useToast';
import translate from '../../../../i18n/translate';

/**
 * Hook for managing topic population when topic is deleted or not selected
 *
 * @param allTopics            Array of all available topics
 * @param changeTopicId        Callback to change the topic id
 * @param currentTopicId       Currently selected topic id
 * @param shouldResetAssignees Whether to reset assignees
 */
const useComposeTopicPopulate = (
  allTopics: TopicBasicFragment[],
  changeTopicId: (topicId: string) => void,
  currentTopicId: string | null,
  shouldResetAssignees: boolean,
) => {
  const pushToast = useToast();
  const topicIdParam = useParamTopicId();

  useEffect(() => {
    // If selected topic is deleted, select the first one
    if (
      !!currentTopicId &&
      allTopics.find(topic => topic.id === currentTopicId) === undefined &&
      allTopics.length > 0
    ) {
      changeTopicId(allTopics[0].id);
      pushToast({
        text: translate('TOPIC__RESET_NO_LONGER_EXISTS'),
        type: 'warning',
      });
    }
    // Adding changeTopicId creates memory leak
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allTopics,
    currentTopicId,
    pushToast,
    shouldResetAssignees,
    topicIdParam,
  ]);

  useEffect(() => {
    if (!currentTopicId && topicIdParam !== null && allTopics.length > 0) {
      changeTopicId(topicIdParam);
    }
    // Run only on when topics are changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTopics.length]);
};

export default useComposeTopicPopulate;
